/**
 * Renders the current article.
 *
 * @module
 */
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import RouterLink from '../componentsInternal/RouterLink';
import ArticleBody from '../componentsInternal/articleBody';
import { ArticleFragmentContext } from '../contexts';
import { useArticleFull } from '../hooks/useArticleFull';
import { useIsArticleActive } from '../hooks/useIsArticleActive';
import { useArticleFragment } from '../hooks/useArticleFragment';
import { ItemChildren } from '../componentsInternal/utils';
import { getArticlePath } from '../utils/routes';
import { throwIfDevelop } from '../utils';
/**
 * Render the title of an article
 *
 * - If you don't pass in a `articleId` then it will use the current article.
 */
export const Title = ({ articleId }) => {
    const article = useArticleFragment({ articleId });
    return React.createElement(React.Fragment, null, article && article.title);
};
Title.displayName = 'Article.Title';
/**
 * Render the summary of an article
 *
 * - If you don't pass in a `articleId` then it will use the current article.
 * - Will fetch the article if it's not already in context.
 */
export const Summary = ({ articleId }) => {
    const article = useArticleFragment({ articleId });
    if (!article || !article.summary)
        return null;
    return React.createElement(React.Fragment, null, article.summary);
};
Summary.displayName = 'Article.Summary';
/**
 * Renders the body of an article.
 *
 * - If you don't pass in an `articleId` then it will use the current article.
 * - Will fetch the article if it's not in context or if its body is not in context.
 */
export const Body = ({ className, noStyles, onClick, }) => {
    const article = useArticleFull();
    const history = useHistory();
    if (!article || !article.body) {
        throwIfDevelop('Can\t use Article.Body outside of the aritcle page');
        return null;
    }
    const onClickInterlink = (articleId) => {
        // NOTE: the slug is missing, but probably not a big deal for interlinks.
        history.push(getArticlePath({ slug: articleId }));
    };
    return (React.createElement(ArticleBody, { articleId: article.id, className: className, html: article.body, noStyles: noStyles, onClickInterlink: onClickInterlink, onClick: onClick }));
};
Body.displayName = 'Article.Body';
/**
 * Render an img tag for the article featured image.
 *
 * - Returns null if no featured imaged is present.
 */
export const FeaturedImage = (props) => {
    const article = useArticleFull();
    if (!article || !article.featuredImageUrl)
        return null;
    return React.createElement("img", { src: article.featuredImageUrl, ...props });
};
FeaturedImage.displayName = 'Article.FeaturedImage';
/**
 * Render the last published time from the current article in the current language.
 */
export const LastPublishedAt = () => {
    const article = useArticleFragment();
    if (!article)
        return null;
    return React.createElement(React.Fragment, null, article.lastPublishedAtLocal);
};
LastPublishedAt.displayName = 'Article.LastUpdatedAt';
/**
 * Render how long ago the article was last published in the current language.
 */
export const LastPublishedAgo = () => {
    const article = useArticleFragment();
    if (!article || !article.lastPublishedAgo)
        return null;
    return React.createElement(React.Fragment, null, article.lastPublishedAgo);
};
LastPublishedAgo.displayName = 'Article.LastPublishedAgo';
/**
 * Render date the article was created in the current language.
 */
export const CreatedAt = () => {
    const article = useArticleFull();
    if (!article)
        return null;
    return React.createElement(React.Fragment, null, article.createdAtLocal);
};
CreatedAt.displayName = 'Article.CreatedAt';
/**
 * Render how long ago the article was created in the current language.
 */
export const CreatedAgo = () => {
    const article = useArticleFull();
    if (!article)
        return null;
    return React.createElement(React.Fragment, null, article.createdAgo);
};
CreatedAgo.displayName = 'Article.CreatedAgo';
/**
 * This will render an `<a/>` tag with a `href` that points to an article page.
 *
 * - If you pass in a `articleId` then it will use it, otherwise it will use the current article context.
 *
 * Usage example with styling of active link:
 *
 * ```javascript
 * import * as Article from '@elevio/kb-kit/lib/components/article';
 *
 * export const ArticleLinksWithStyling = () => {
 *   // These styles will apply if the link matches the current article:
 *   const activeStyle: React.CSSProperties = {
 *     color: 'red',
 *   };
 *
 *   return (
 *     <div>
 *       <Article.Link articleId="123" activeStyle={activeStyle}>
 *         How to reset your password
 *       </Article.Link>
 *       <Article.Link articleId="456" activeStyle={activeStyle}>
 *         How to contact us
 *       </Article.Link>
 *     </div>
 *   );
 * };
 * ```
 */
export const Link = ({ children, articleId, slug, ...props }) => {
    const fragment = useArticleFragment({ articleId });
    const isActive = useIsArticleActive((fragment === null || fragment === void 0 ? void 0 : fragment.id) || articleId);
    let path = null;
    if (fragment) {
        path = fragment.path;
    }
    else if (slug) {
        path = getArticlePath({ slug });
    }
    else if (articleId) {
        path = getArticlePath({ slug: articleId });
    }
    if (!path)
        return null;
    return (React.createElement(RouterLink, { to: path, isActive: () => isActive, ...props }, children));
};
Link.displayName = 'Article.Link';
/**
 * Renders out the child content if the article either from the `articleId` prop or, if not provided, the currently active article.
 *
 * - If a render prop is passed as the child, then a boolean will be passed into the render function with the 'is-active' state.
 */
export const IsActive = ({ children, articleId }) => {
    const isActive = useIsArticleActive(articleId);
    if (typeof children === 'function') {
        return React.createElement(React.Fragment, null, children(isActive));
    }
    if (!isActive)
        return null;
    return React.createElement(React.Fragment, null, children);
};
IsActive.displayName = 'Article.IsActive';
/**
 * Renders out the child content if the article either from the `articleId` prop or if not provided the current article is NOT active.
 *
 * If a render prop is passed as the child then a boolean will be provided with the 'is-non-active' state.
 */
export const IsNotActive = ({ children, articleId, }) => {
    const isActive = useIsArticleActive(articleId);
    if (typeof children === 'function') {
        return React.createElement(React.Fragment, null, children(!isActive));
    }
    if (isActive)
        return null;
    return React.createElement(React.Fragment, null, children);
};
IsNotActive.displayName = 'Article.IsNotActive';
/**
 * Returns the current article page data as a render prop
 */
export function RenderFull({ children }) {
    const article = useArticleFull();
    if (!article)
        return null;
    return (React.createElement(ItemChildren, { Provider: ArticleFragmentContext.Provider, data: article }, children));
}
RenderFull.displayName = 'Article.RenderFull';
export function RenderFragment({ children, articleId }) {
    const article = useArticleFragment({ articleId });
    if (!article)
        return null;
    return (React.createElement(ItemChildren, { Provider: ArticleFragmentContext.Provider, data: article }, children));
}
RenderFragment.displayName = 'Article.RenderFragment';
//# sourceMappingURL=article.js.map